/**
 * Collector-number identity for a printing.
 *
 * Riftcodex's `collector_number` is a bare integer shared by related prints:
 *   - base + alternate art (`51` for both; letter lives in riftbound_id `051`/`051a`)
 *   - overnumbered + signed showcase (`304` for both; star lives in `ogn-304*` vs `ogn-304`)
 * Prefer the riftbound segment whenever it is present so slugs, lookups, and
 * printing keys stay unique.
 *
 * Signed/star markers are normalized to a trailing `s` (`304*` / `195-star` →
 * `304s` / `195s`) so they survive URL slugs (`[a-z0-9]+`) and stay distinct
 * from the non-signed twin.
 *
 * Framework-free so API + SPA share one definition.
 */

/**
 * Canonical collector token for identity / slugs / printing keys.
 * Lowercase, trim; map trailing stars (and `N-star`) to a trailing `s`.
 */
export function normalizeCollectorNumber(num) {
  let s = String(num ?? '')
    .trim()
    .toLowerCase()
    .replace(/★+/g, '*')
    .replace(/\*+$/g, 's');
  // "195-star" (VEN three-part middle) → "195s"
  s = s.replace(/^(\d+)-star$/, '$1s');
  return s;
}

/**
 * Strip leading zeros from the numeric prefix while keeping a letter suffix.
 * "042" → "42", "051a" → "51a", "304s" → "304s", "T01" → "t01".
 */
export function normalizeCardNumber(num) {
  const s = normalizeCollectorNumber(num);
  if (!s) return '';
  const m = s.match(/^0*(\d+)([a-z]*)$/);
  if (m) return m[1] + m[2];
  return s;
}

/**
 * Collector number from either `VEN-140` or `ven-140-166` (and signed
 * `ogn-304*-298` / `ven-195-star-166`). Returns '' when the id is not a
 * set-number form.
 */
export function collectorNumberFromRiftboundId(riftboundId) {
  const parts = String(riftboundId || '')
    .trim()
    .split('-')
    .filter(Boolean);
  if (parts.length < 2) return '';
  if (parts.length === 2) return normalizeCollectorNumber(parts[1]);
  return normalizeCollectorNumber(parts.slice(1, -1).join('-'));
}

export function setCodeFromRiftboundId(riftboundId) {
  const head = String(riftboundId || '').trim().split('-')[0];
  return head ? head.toUpperCase() : '';
}

/**
 * Canonical collector number for a card: prefer the riftbound_id segment
 * (keeps alt-art / signed suffixes), fall back to the stored `number` field.
 *
 * @param {{ number?: string | number | null, riftboundId?: string | null, riftbound_id?: string | null }} card
 */
export function cardCollectorNumber(card) {
  if (!card) return '';
  const fromRiftbound = collectorNumberFromRiftboundId(card.riftboundId || card.riftbound_id);
  const fromNumber =
    card.number == null || card.number === '' ? '' : normalizeCollectorNumber(card.number);
  // Promo print runs keep the distinguishing suffix on `number`
  // (`015-167`, `015-167-champion`). A 3-part riftbound id parses the last
  // segment as a set size (`opp-015-167` → `015`) and would collapse that
  // printing onto another card's URL. Prefer the longer stored collector.
  if (fromRiftbound && fromNumber && fromNumber.startsWith(`${fromRiftbound}-`)) {
    return fromNumber;
  }
  if (fromRiftbound) return fromRiftbound;
  return fromNumber;
}

/**
 * Bare numeric collector for marketplace blueprint lookup — signed `304s`
 * and overnumbered `304` share a CardTrader / Cardmarket product id.
 */
export function collectorNumberForMarketMatch(num) {
  return normalizeCardNumber(num).replace(/(\d+)s$/, '$1');
}

/** Zero-pad pure numeric collectors for URL slugs; keep alphanumeric as-is. */
export function formatCardNumber(num) {
  if (num == null || num === '') return '000';
  const s = String(num).trim();
  if (!s) return '000';
  return /^\d+$/.test(s) ? s.padStart(3, '0') : s.toLowerCase();
}

/**
 * True when two collector strings refer to the same printing after zero-stripping.
 * "051" matches "51"; "051a" matches "51a"; "051" does not match "051a";
 * "304" does not match "304s".
 */
export function collectorNumbersEqual(a, b) {
  const left = normalizeCardNumber(a);
  const right = normalizeCardNumber(b);
  return Boolean(left) && left === right;
}
